/**
 * EnhancedAnomalyFingerprint - Visual pattern recognition display
 * Shows feature contributions with enhanced visuals and interactivity
 */

import { useState, useMemo } from 'react'
import { Card } from '@/components/common'
import clsx from 'clsx'
import {
  Fingerprint,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Info,
  ChevronDown,
  History,
  Target,
  Activity,
  Zap
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface Feature {
  name: string
  contribution: number
  direction: 'high' | 'low' | 'normal'
  trend?: 'increasing' | 'decreasing' | 'stable'
  threshold?: number
  currentValue?: number
  unit?: string
}

interface HistoricalMatch {
  date: string
  resolution: string
  similarity: number
}

interface EnhancedAnomalyFingerprintProps {
  category: string
  features: Feature[]
  historicalMatches?: number
  matchDetails?: HistoricalMatch[]
  patternConfidence?: number
  lastDetected?: string
  className?: string
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const CATEGORY_CONFIG: Record<string, {
  label: string
  description: string
  icon: typeof AlertTriangle
  color: string
  bgColor: string
}> = {
  sensor_drift: {
    label: 'Sensor Drift',
    description: 'Gradual deviation from calibrated baseline readings',
    icon: Activity,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10'
  },
  process_deviation: {
    label: 'Process Deviation',
    description: 'Operating parameters outside normal process envelope',
    icon: TrendingUp,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10'
  },
  vibration: {
    label: 'Vibration Anomaly',
    description: 'Abnormal mechanical vibration patterns detected',
    icon: Zap,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10'
  },
  thermal: {
    label: 'Thermal Anomaly',
    description: 'Temperature patterns indicate potential issues',
    icon: Target,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10'
  }
}

const DIRECTION_CONFIG = {
  high: {
    label: 'Above Normal',
    color: 'text-red-400',
    bgColor: 'bg-red-500',
    barColor: 'from-red-600 to-red-400',
    icon: TrendingUp
  },
  low: {
    label: 'Below Normal',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500',
    barColor: 'from-blue-600 to-blue-400',
    icon: TrendingDown
  },
  normal: {
    label: 'Normal',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500',
    barColor: 'from-emerald-600 to-emerald-400',
    icon: Minus
  }
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Circular contribution indicator */
function ContributionRing({
  contribution,
  size = 48,
  strokeWidth = 4
}: {
  contribution: number
  size?: number
  strokeWidth?: number
}) {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (contribution / 100) * circumference

  const getColor = () => {
    if (contribution >= 25) return '#ef4444'
    if (contribution >= 15) return '#f59e0b'
    return '#3b82f6'
  }

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-surface-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold font-mono text-surface-200">
          {contribution}%
        </span>
      </div>
    </div>
  )
}

/** Feature contribution row with enhanced visuals */
function FeatureRow({
  feature,
  rank,
  maxContribution
}: {
  feature: Feature
  rank: number
  maxContribution: number
}) {
  const [showDetails, setShowDetails] = useState(false)
  const directionConfig = DIRECTION_CONFIG[feature.direction]
  const DirectionIcon = directionConfig.icon
  const percentage = (feature.contribution / maxContribution) * 100

  const TrendIcon = feature.trend === 'increasing' ? TrendingUp :
                    feature.trend === 'decreasing' ? TrendingDown : Minus

  return (
    <div className="group">
      <div
        className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer"
        onClick={() => setShowDetails(!showDetails)}
      >
        {/* Rank indicator */}
        <div className={clsx(
          'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
          rank <= 3 ? 'bg-primary-500/20 text-primary-400' : 'bg-surface-700 text-surface-400'
        )}>
          {rank}
        </div>

        {/* Contribution ring */}
        <ContributionRing contribution={feature.contribution} />

        {/* Feature info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base font-medium text-surface-200 truncate">
              {feature.name}
            </span>
            <div className={clsx(
              'flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
              directionConfig.color,
              feature.direction === 'high' ? 'bg-red-500/10' :
              feature.direction === 'low' ? 'bg-blue-500/10' : 'bg-emerald-500/10'
            )}>
              <DirectionIcon size={12} />
              {directionConfig.label}
            </div>
          </div>

          {/* Contribution bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-surface-700 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full bg-gradient-to-r transition-all duration-500',
                  directionConfig.barColor
                )}
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="text-sm font-mono font-bold text-surface-300 min-w-[4rem] text-right">
              {feature.contribution.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* Trend indicator */}
        {feature.trend && (
          <div className={clsx(
            'flex items-center gap-1 text-xs',
            feature.trend === 'increasing' ? 'text-red-400' :
            feature.trend === 'decreasing' ? 'text-emerald-400' : 'text-surface-500'
          )}>
            <TrendIcon size={14} />
          </div>
        )}

        {/* Expand indicator */}
        <ChevronDown
          size={16}
          className={clsx(
            'text-surface-500 transition-transform',
            showDetails && 'rotate-180'
          )}
        />
      </div>

      {/* Expanded details */}
      {showDetails && (feature.currentValue !== undefined || feature.threshold) && (
        <div className="mt-2 ml-12 p-3 bg-surface-900/50 rounded-lg border border-surface-700 grid grid-cols-2 gap-4">
          {feature.currentValue !== undefined && (
            <div>
              <span className="text-xs text-surface-500 uppercase tracking-wider">Current Value</span>
              <p className="text-lg font-mono font-bold text-surface-200 mt-1">
                {feature.currentValue}{feature.unit || ''}
              </p>
            </div>
          )}
          {feature.threshold !== undefined && (
            <div>
              <span className="text-xs text-surface-500 uppercase tracking-wider">Threshold</span>
              <p className="text-lg font-mono font-bold text-surface-400 mt-1">
                {feature.threshold}{feature.unit || ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Historical match card */
function MatchCard({ match }: { match: HistoricalMatch }) {
  return (
    <div className="p-3 bg-surface-800/50 rounded-lg border border-surface-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-surface-500">{match.date}</span>
        <span className={clsx(
          'text-xs font-medium px-2 py-0.5 rounded',
          match.similarity >= 90 ? 'bg-emerald-500/20 text-emerald-400' :
          match.similarity >= 70 ? 'bg-amber-500/20 text-amber-400' :
          'bg-surface-700 text-surface-400'
        )}>
          {match.similarity}% match
        </span>
      </div>
      <p className="text-sm text-surface-300">{match.resolution}</p>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function EnhancedAnomalyFingerprint({
  category,
  features,
  historicalMatches = 0,
  matchDetails = [],
  patternConfidence = 85,
  lastDetected,
  className
}: EnhancedAnomalyFingerprintProps) {
  const [showHistory, setShowHistory] = useState(false)

  const categoryConfig = CATEGORY_CONFIG[category] || {
    label: category.replace('_', ' '),
    description: 'Anomaly pattern detected',
    icon: AlertTriangle,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10'
  }

  const maxContribution = useMemo(() =>
    Math.max(...features.map(f => f.contribution), 1),
    [features]
  )

  const sortedFeatures = useMemo(() =>
    [...features].sort((a, b) => b.contribution - a.contribution),
    [features]
  )

  const totalContribution = useMemo(() =>
    features.reduce((sum, f) => sum + f.contribution, 0),
    [features]
  )

  return (
    <Card className={className}>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={clsx(
            'p-3 rounded-xl',
            categoryConfig.bgColor
          )}>
            <Fingerprint size={28} className={categoryConfig.color} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
              Anomaly Fingerprint
              <span className={clsx(
                'text-sm font-normal px-2 py-0.5 rounded',
                categoryConfig.bgColor,
                categoryConfig.color
              )}>
                {categoryConfig.label}
              </span>
            </h3>
            <p className="text-sm text-surface-400 mt-1">
              {categoryConfig.description}
            </p>
          </div>
        </div>

        {/* Stats badges */}
        <div className="flex items-center gap-3">
          <div className="text-center px-4 py-2 bg-surface-800 rounded-lg border border-surface-700">
            <div className="text-xl font-bold font-mono text-primary-400">
              {patternConfidence}%
            </div>
            <div className="text-xs text-surface-500">Confidence</div>
          </div>
          {historicalMatches > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={clsx(
                'text-center px-4 py-2 rounded-lg border transition-colors',
                showHistory
                  ? 'bg-primary-500/20 border-primary-500/30 text-primary-400'
                  : 'bg-surface-800 border-surface-700 text-surface-300 hover:border-surface-600'
              )}
            >
              <div className="flex items-center gap-1.5">
                <History size={16} />
                <span className="text-xl font-bold font-mono">{historicalMatches}</span>
              </div>
              <div className="text-xs text-surface-500">Matches</div>
            </button>
          )}
        </div>
      </div>

      {/* Historical matches panel */}
      {showHistory && matchDetails.length > 0 && (
        <div className="mb-6 p-4 bg-surface-900/50 rounded-xl border border-surface-700">
          <h4 className="text-sm font-semibold text-surface-200 mb-3 flex items-center gap-2">
            <History size={16} className="text-primary-400" />
            Historical Pattern Matches
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {matchDetails.map((match, i) => (
              <MatchCard key={i} match={match} />
            ))}
          </div>
        </div>
      )}

      {/* Feature contributions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-surface-200 uppercase tracking-wider flex items-center gap-2">
            <Activity size={16} className="text-primary-400" />
            Feature Contributions
          </h4>
          <span className="text-xs text-surface-500">
            Total: <span className="font-mono font-bold text-surface-300">{totalContribution.toFixed(1)}%</span>
          </span>
        </div>

        {sortedFeatures.map((feature, index) => (
          <FeatureRow
            key={feature.name}
            feature={feature}
            rank={index + 1}
            maxContribution={maxContribution}
          />
        ))}
      </div>

      {/* Footer info */}
      {lastDetected && (
        <div className="mt-6 pt-4 border-t border-surface-700 flex items-center justify-between text-xs text-surface-500">
          <span>Pattern first detected: {lastDetected}</span>
          <button className="flex items-center gap-1 text-primary-400 hover:text-primary-300 transition-colors">
            <Info size={14} />
            Learn about this pattern
          </button>
        </div>
      )}
    </Card>
  )
}
